/**
 * Models API Hook
 */

import { useApiData, useAuthenticatedFetch } from './client';
import type { ModelAssociation } from './types';

export function useModels(options?: {
  limit?: number;
  orderBy?: string;
  ascending?: boolean;
}) {
  // NOTE: api-edna accepts `limit`, `order_by`, and `ascending` only. It has no
  // `skip`/`offset`/cursor, and no eager-loading flags — collections are capped at
  // 1000 with truncation signalled in the `X-Result-Truncated` response header.
  // Options it ignores were removed rather than left as silent no-ops.
  const params = new URLSearchParams();
  if (options?.limit !== undefined) params.set('limit', String(options.limit));
  if (options?.orderBy) params.set('order_by', options.orderBy);
  if (options?.ascending !== undefined) params.set('ascending', String(options.ascending));

  const queryString = params.toString();
  const endpoint = queryString ? `/models?${queryString}` : '/models';

  return useApiData<ModelAssociation[]>(endpoint);
}

export function useModelMutations() {
  const { fetchData } = useAuthenticatedFetch<ModelAssociation>();

  const createModel = (data: {
    name: string;
    id_project: number;
    active: boolean;
    result_table_name: string;
    max_token_limit: number;
  }) =>
    fetchData('/models', {
      method: 'POST',
      body: JSON.stringify(data),
    });

  const updateModel = (id: number, data: Partial<ModelAssociation>) =>
    fetchData(`/models/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });

  const deleteModel = (id: number) =>
    fetchData(`/models/${id}`, { method: 'DELETE' });

  return { createModel, updateModel, deleteModel };
}
